import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Github, Linkedin, Mail, Send } from 'lucide-react';

// Contact component for handling user inquiries and form submissions
// Uses FormSubmit.co as primary service with mailto fallback
const Contact = () => {
  // Toast notification system for user feedback
  const { toast } = useToast();
  
  // Form state management for user input
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  
  // Loading state for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle input changes for all form fields
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submission started', formData); // Debug log
    setIsSubmitting(true);
    
    try {
      // Form validation
      if (!formData.name || !formData.email || !formData.message) {
        throw new Error("Please complete all fields");
      }
      
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error("Please enter a valid email address");
      }
      
      console.log('Form validation passed, attempting submission...'); // Debug log
      
      // Try FormSubmit.co first
      try {
        console.log('Sending to FormSubmit.co...'); // Debug log
        const response = await fetch("https://formsubmit.co/matthewinnes42@gmail.com", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            message: formData.message,
            _subject: `Portfolio Contact: Message from ${formData.name}`,
            _captcha: false, // Disable captcha for better UX
            _template: "table", // Use table template for better formatting
            _next: window.location.href, // Redirect back to the same page
          }),
        });
        
        console.log('FormSubmit.co response:', response.status, response.statusText); // Debug log
        
        // Get the response text to check what FormSubmit.co actually returned
        const responseText = await response.text();
        console.log('FormSubmit.co response text:', responseText.substring(0, 500)); // Debug log
        
        // Check for specific FormSubmit.co responses
        if (responseText.includes("Thank you for your message") || 
            responseText.includes("Your message has been sent") ||
            responseText.includes("successfully sent")) {
          console.log('FormSubmit.co success!'); // Debug log
          toast({
            title: "Message sent successfully!",
            description: "Thank you for reaching out. I'll get back to you soon.",
          });
          
          // Reset form after successful submission
          setFormData({ name: '', email: '', message: '' });
          return; // Exit early on success
        }
        
        // Check if FormSubmit.co is asking for email verification
        if (responseText.includes("Please check your email") || 
            responseText.includes("verification") ||
            responseText.includes("confirm your email")) {
          console.log('FormSubmit.co requires email verification'); // Debug log
          toast({
            title: "Email verification required",
            description: "Please check your email for a verification link from FormSubmit.co, then try submitting again.",
            variant: "destructive"
          });
          return; // Exit early - don't use fallback yet
        }
        
        // If we get here, FormSubmit.co didn't work as expected
        console.warn('FormSubmit.co returned unexpected response, trying alternative method...');
        
      } catch (formSubmitError) {
        console.warn('FormSubmit.co error:', formSubmitError);
      }
      
      // Alternative: Use mailto link as fallback
      console.log('Using mailto fallback...'); // Debug log
      const mailtoLink = `mailto:matthewinnes42@gmail.com?subject=Portfolio Contact: Message from ${encodeURIComponent(formData.name)}&body=${encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`)}`;
      
      // Try to open email client
      try {
        window.open(mailtoLink, '_blank');
        
        toast({
          title: "Email client opened",
          description: "Your default email client has been opened with your message. Please send the email manually.",
        });
      } catch (emailError) {
        console.error('Failed to open email client:', emailError);
        
        // If email client fails, show the email details for manual copying
        const emailDetails = `To: matthewinnes42@gmail.com\nSubject: Portfolio Contact: Message from ${formData.name}\n\nName: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`;
        
        toast({
          title: "Email details",
          description: "Please copy the email details and send manually to matthewinnes42@gmail.com",
        });
        
        // Log the email details to console for easy copying
        console.log('Email details to send manually:', emailDetails);
      }
      
      // Reset form
      setFormData({ name: '', email: '', message: '' });
      
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Something went wrong",
        description: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
      console.log('Form submission completed'); // Debug log
    }
  };

  // Social media links for additional contact methods
  const socialLinks = [
    { icon: <Github size={20} />, url: 'https://github.com', label: 'GitHub' },
    { icon: <Linkedin size={20} />, url: 'https://linkedin.com', label: 'LinkedIn' },
    { icon: <Mail size={20} />, url: 'mailto:matthewinnes42@gmail.com', label: 'Email' },
  ];

  return (
    // Contact section with dark mode support and responsive design
    <section id="contact" className="py-24 bg-background dark:bg-navy/30">
      <div className="container mx-auto px-6">
        {/* Section header with title and description */}
        <div className="flex flex-col items-center mb-12 text-center">
          <p className="text-teal mb-2">05. What's Next?</p>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground dark:text-lightestSlate mb-4">Get In Touch</h2>
          <p className="text-foreground dark:text-lightSlate max-w-xl mb-8 font-medium">
            I'm currently looking for new opportunities. Whether you have a question or just want to ask advice, drop me a message and I will be sure to get back to you!
          </p>
        </div>
        
        {/* Contact form container with styling and shadow */}
        <div className="max-w-2xl mx-auto bg-white dark:bg-navy/40 p-8 rounded-lg border border-border dark:border-slate/20 shadow-lg">
          {/* Form verification notice */}
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Note:</strong> On your first submission, you'll receive a verification email from FormSubmit.co. 
              Please check your email and click the verification link to activate the form service.
            </p>
          </div>
          
          {/* Main contact form with validation and submission handling */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name and Email fields in a responsive grid */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground dark:text-lightSlate mb-2">
                  Name
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-background/50 dark:bg-navy/60 border-border dark:border-slate/30 focus:border-teal text-foreground dark:text-lightSlate"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground dark:text-lightSlate mb-2">
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-background/50 dark:bg-navy/60 border-border dark:border-slate/30 focus:border-teal text-foreground dark:text-lightSlate"
                  placeholder="Your email"
                />
              </div>
            </div>
            
            {/* Message textarea for longer content */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-foreground dark:text-lightSlate mb-2">
                Message
              </label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                className="w-full min-h-[150px] bg-background/50 dark:bg-navy/60 border-border dark:border-slate/30 focus:border-teal text-foreground dark:text-lightSlate"
                placeholder="Your message"
              />
            </div>
            
            {/* Submit button with loading state */}
            <div className="flex justify-center">
              <Button 
                type="submit"
                className="bg-teal hover:bg-teal/90 text-navy px-8 py-6 text-lg flex items-center"
                disabled={isSubmitting}
              >
                <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                <Send className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </form>
          
          {/* Social media links section */}
          <div className="mt-16 flex justify-center">
            <div className="flex space-x-6">
              {socialLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground dark:text-lightSlate hover:text-teal dark:hover:text-teal transition-colors duration-300"
                  aria-label={link.label}
                >
                  {link.icon}
                </a>
              ))}
            </div>
          </div>
          
          {/* Direct email contact option */}
          <div className="mt-8 text-center">
            <p className="text-sm text-foreground dark:text-lightSlate mb-4">
              Having trouble with the form? You can also contact me directly:
            </p>
            <a
              href="mailto:matthewinnes42@gmail.com"
              className="inline-flex items-center gap-2 text-teal hover:text-teal/80 transition-colors"
            >
              <Mail size={16} />
              matthewinnes42@gmail.com
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
